import { useEffect, useMemo, useState, type CSSProperties } from "react";
import {
  checkedPreviewBlob,
  defaultAssetUrl,
  type AssetLoader,
} from "./images";

/** A host-loaded thumbnail never falls back to an unauthenticated resource URL. */
export function AssetImage({
  assetId,
  assetUrl = defaultAssetUrl,
  loadAsset,
  alt,
  className,
  style,
  retryable = true,
}: {
  assetId: string;
  assetUrl?: (id: string) => string;
  loadAsset?: AssetLoader;
  alt: string;
  className?: string;
  style?: CSSProperties;
  retryable?: boolean;
}) {
  const [attempt, retry] = useState(0);
  const key = useMemo(() => ({}), [assetId, loadAsset, attempt]);
  const [state, setState] = useState<{
    key: object;
    url?: string;
    error?: boolean;
  }>();
  useEffect(() => {
    if (!loadAsset || !assetId) return;
    const controller = new AbortController();
    let live = true,
      url: string | undefined;
    void Promise.resolve()
      .then(() => {
        if (!live) throw new Error();
        return loadAsset(assetId, controller.signal);
      })
      .then(async (blob) => {
        if (!live) return;
        url = URL.createObjectURL(checkedPreviewBlob(blob));
        const image = new Image();
        image.src = url;
        await image.decode();
        if (!live) return;
        if (
          !image.naturalWidth ||
          !image.naturalHeight ||
          image.naturalWidth * image.naturalHeight > 40000000
        )
          throw new Error();
        setState({ key, url });
      })
      .catch(() => {
        if (url) {
          URL.revokeObjectURL(url);
          url = undefined;
        }
        if (live) setState({ key, error: true });
      });
    return () => {
      live = false;
      controller.abort();
      if (url) URL.revokeObjectURL(url);
      url = undefined;
    };
  }, [key]);
  if (!loadAsset)
    return (
      <img
        src={assetUrl(assetId)}
        alt={alt}
        className={className}
        style={style}
      />
    );
  const current = state?.key === key ? state : undefined;
  return current?.url ? (
    <img src={current.url} alt={alt} className={className} style={style} />
  ) : current?.error ? (
    <span>
      <span role="alert">{alt}: image preview unavailable</span>{" "}
      {retryable && (
        <button
          type="button"
          onClick={(event) => {
            // Retrying a preview inside a choice card must not select it.
            event.preventDefault();
            event.stopPropagation();
            retry((value) => value + 1);
          }}
        >
          Retry {alt}
        </button>
      )}
    </span>
  ) : (
    <span role="status">Preparing {alt}</span>
  );
}
